import {
  CoCreationFormElement,
  QuestionElement,
  SectionElement,
} from '@factorialco/f0-react/dist/experimental'
import { FormDetailFragment, UpdateFormMutationVariables } from 'generated/resources/graphql'
import { FormsElementInput } from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import i18n from 'modules/core/lib/i18n'

import { QuestionType, QuestionnaireType, SectionType } from './types'

export const defaultSections = (type: 'effectiveness' | 'satisfaction'): FormsElementInput => {
  const { lableSection, labelScore, scoreType } =
    type === 'effectiveness'
      ? {
          lableSection: i18n.t(
            'trainings.surveys.create.effectiveness.overall_effectiveness.title'
          ),
          labelScore: i18n.t(
            'trainings.surveys.create.effectiveness.overall_effectiveness.content'
          ),
          scoreType: 'courseeffectiveness',
        }
      : {
          lableSection: i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.title'),
          labelScore: i18n.t('trainings.surveys.create.satisfaction.overall_satisfaction.content'),
          scoreType: 'coursesatisfaction',
        }
  return {
    label: lableSection,
    description: '',
    type: 'section',
    position: 0,
    mandatory: true,
    params: {
      analyzeWithAi: null,
      scoreType: null,
      maxLength: null,
      optionalComment: null,
      locked: true,
    },
    elements: [
      {
        label: labelScore,
        description: '',
        type: 'score',
        position: 0,
        mandatory: true,
        params: {
          scoreType,
          locked: true,
        },
        hasCorrectAnswer: false,
        options: [
          {
            label: i18n.t('forms.default_rating_scale.very_low'),
            value: '1',
            id: '',
            isCorrect: false,
          },
          { label: i18n.t('forms.default_rating_scale.low'), value: '2', id: '', isCorrect: false },
          {
            label: i18n.t('forms.default_rating_scale.average'),
            value: '3',
            id: '',
            isCorrect: false,
          },
          {
            label: i18n.t('forms.default_rating_scale.high'),
            value: '4',
            id: '',
            isCorrect: false,
          },
          {
            label: i18n.t('forms.default_rating_scale.very_high'),
            value: '5',
            id: '',
            isCorrect: false,
          },
        ],
      },
    ],
  }
}

const createQuestionWithOptions = (
  baseProperties: {
    id: string
    title: string
    description: string
    required: boolean
    locked: boolean
  },
  mappedType: 'select' | 'multi-select' | 'rating',
  question: QuestionType
): QuestionElement => {
  if (mappedType === 'select' || mappedType === 'multi-select') {
    return {
      ...baseProperties,
      type: mappedType,
      options:
        question.options?.map((option) => ({
          value: option.value,
          label: option.label,
          correct: option.isCorrect,
        })) ?? [],
    }
  }

  // rating type
  return {
    ...baseProperties,
    type: 'rating' as const,
    options:
      question.options?.map((option) => ({
        value: Number(option.value),
        label: option.label,
      })) ?? [],
  }
}

const createQuestionProperty = (question: QuestionType): QuestionElement => {
  const mappedType = mapQuestionType(question.type)
  const baseProperties = {
    id: question.id || uuid(),
    title: question.label,
    description: question.description,
    required: question.mandatory,
    locked: question.locked,
  }

  switch (mappedType) {
    case 'select':
    case 'multi-select':
    case 'rating':
      return createQuestionWithOptions(baseProperties, mappedType, question)
    case 'text':
      return { ...baseProperties, type: 'text' as const }
    case 'longText':
      return { ...baseProperties, type: 'longText' as const }
    case 'date':
      return { ...baseProperties, type: 'date' as const }
    case 'link':
      return { ...baseProperties, type: 'link' as const }
    case 'numeric':
      return { ...baseProperties, type: 'numeric' as const }
    default:
      return { ...baseProperties, type: 'text' as const }
  }
}

const createSectionProperty = (section: SectionType): SectionElement => {
  return {
    id: section.id || uuid(),
    title: section.label,
    description: section.description,
    locked: section.locked,
    questions: section.elements.map((element) => createQuestionProperty(element)),
  }
}

const mapQuestionType = (
  type: QuestionType['type']
): 'text' | 'select' | 'rating' | 'date' | 'link' | 'multi-select' | 'numeric' | 'longText' => {
  switch (type) {
    case 'text':
      return 'text'
    case 'longtext':
      return 'longText'
    case 'select':
      return 'select'
    case 'multiselect':
      return 'multi-select'
    case 'number':
      return 'numeric'
    case 'rating':
      return 'rating'
    case 'date':
      return 'date'
    case 'url':
      return 'link'
    case 'dropdownsingle':
      return 'select'
    case 'score':
      return 'rating' // fallback to rating for score
    default:
      return 'text'
  }
}

const mapRevertQuestionType = (
  type:
    | 'text'
    | 'select'
    | 'rating'
    | 'date'
    | 'link'
    | 'multi-select'
    | 'numeric'
    | 'longText'
    | 'dropdown-single'
): QuestionType['type'] => {
  switch (type) {
    case 'text':
      return 'text'
    case 'select':
      return 'select'
    case 'rating':
      return 'rating'
    case 'date':
      return 'date'
    case 'link':
      return 'url'
    case 'multi-select':
      return 'multiselect'
    case 'numeric':
      return 'number'
    case 'longText':
      return 'longtext'
    case 'dropdown-single':
      return 'dropdownsingle'
    default:
      return 'text'
  }
}

const SectionElementToQuestionnaireElement = (
  section: SectionElement,
  index: number
): SectionType => {
  return {
    id: section.id,
    label: section.title,
    description: section.description ?? '',
    type: 'section',
    position: index,
    mandatory: false,
    locked: section.locked ?? false,
    params: {},
    options: [],
    elements:
      section.questions?.map((question, questionIndex) =>
        QuestionElementToQuestionnaireElement(question, questionIndex)
      ) ?? [],
  }
}

const QuestionElementToQuestionnaireElement = (
  question: QuestionElement,
  index: number
): QuestionType => {
  return {
    id: question.id,
    label: question.title,
    description: question.description ?? '',
    type: mapRevertQuestionType(question.type),
    position: index,
    mandatory: question.required ?? false,
    locked: question.locked ?? false,
    params: {},
    options:
      'options' in question && question.options
        ? question.options.map((option) => ({
            value: option.value.toString(),
            label: option.label,
            isCorrect: 'correct' in option ? (option.correct ?? false) : false,
          }))
        : null,
  }
}

export const FromQuestionnaireToForm = (
  elements: QuestionnaireType['elements']
): CoCreationFormElement[] =>
  elements.map((element) => {
    if (element.type === 'section') {
      return {
        type: 'section' as const,
        section: createSectionProperty(element),
      }
    }
    return {
      type: 'question' as const,
      question: createQuestionProperty(element),
    }
  })

export const FromFormToQuestionnaire = (
  elements: CoCreationFormElement[]
): QuestionnaireType['elements'] => {
  return elements
    .map((element, index) =>
      element.type === 'section'
        ? SectionElementToQuestionnaireElement(element.section, index)
        : QuestionElementToQuestionnaireElement(element.question, index)
    )
    .flat()
}

const shouldNullifyId = (id: string | undefined): boolean => {
  return !!id && id.startsWith('new-')
}

export const nullifyIdIfNew = (id: string | undefined): string | null | undefined => {
  return shouldNullifyId(id) ? null : id
}

export const FromQuestionaireToMutationVariables = (
  formId: string,
  anonymous: boolean,
  form: QuestionnaireType
): UpdateFormMutationVariables => {
  return {
    id: formId,
    anonymous: anonymous,
    description: form.description,
    title: form.title,
    elements:
      form.elements.map((element) => {
        return {
          id: nullifyIdIfNew(element.id),
          label: element.label,
          description: element.description ?? '',
          type: element.type,
          position: element.position,
          mandatory: element.mandatory,
          params: {
            analyzeWithAi: null,
            scoreType: null,
            maxLength: null,
            optionalComment: null,
            locked: element.locked,
          },
          options: element.options
            ? element.options.map((option) => ({
                id: nullifyIdIfNew(option.id),
                value: option.value,
                label: option.label,
                isCorrect: option.isCorrect,
              }))
            : null,
          elements:
            element.type === 'section' && 'elements' in element
              ? element.elements.map((nestedElement) => ({
                  id: nullifyIdIfNew(nestedElement.id),
                  label: nestedElement.label,
                  description: nestedElement.description,
                  type: nestedElement.type,
                  position: nestedElement.position,
                  mandatory: nestedElement.mandatory,
                  params: {
                    analyzeWithAi: null,
                    scoreType: null,
                    maxLength: null,
                    optionalComment: null,
                    locked: nestedElement.locked,
                  },
                  hasCorrectAnswer: nestedElement.hasCorrectAnswer,
                  options: nestedElement.options
                    ? nestedElement.options.map((option) => ({
                        id: nullifyIdIfNew(option.id),
                        value: option.value,
                        label: option.label,
                        isCorrect: option.isCorrect,
                      }))
                    : null,
                }))
              : undefined,
        }
      }) ?? [],
  }
}

const findSectionElements = (
  elements: FormDetailFragment['elements'],
  sectionId: string
): FormDetailFragment['elements'] => {
  return elements.filter((element) => element.sectionId === sectionId)
}

const fragmentQuestionToElement = (
  element: FormDetailFragment['elements'][number],
  elements: FormDetailFragment['elements']
): SectionType => {
  const sectionQuestions = findSectionElements(elements, element.id)
    .map((sectionElement) => fragmentElementToQuestionnaireElement(sectionElement, false))
    .filter((sectionElement): sectionElement is QuestionType => sectionElement !== undefined)
    .sort((a, b) => a.position - b.position)

  return {
    id: element.id,
    label: element.label,
    description: element.description ?? '',
    type: 'section',
    position: element.position,
    mandatory: element.mandatory,
    locked: element.params.locked ?? false,
    params: element.params,
    options: element.options,
    hasCorrectAnswer: element.hasCorrectAnswer,
    elements: sectionQuestions,
  }
}

const fragmentElementToQuestionnaireElement = (
  element: FormDetailFragment['elements'][number],
  rootElements: boolean
): QuestionType | undefined => {
  if (rootElements && element.sectionId) {
    return undefined
  }
  if (element.type === 'section') {
    return undefined
  }
  return {
    id: element.id,
    label: element.label,
    description: element.description ?? '',
    type: element.type,
    position: element.position,
    mandatory: element.mandatory,
    locked: element.params.locked ?? false,
    params: element.params,
    options: element.options,
    hasCorrectAnswer: element.hasCorrectAnswer,
  }
}

export const FromFragmentToQuestionnaire = (data: FormDetailFragment): QuestionnaireType => {
  const elements: QuestionnaireType['elements'] = data.elements
    .map((element) => {
      if (element.type === 'section') {
        return fragmentQuestionToElement(element, data.elements)
      } else {
        return fragmentElementToQuestionnaireElement(element, true)
      }
    })
    .filter((element): element is QuestionnaireType['elements'][number] => element !== undefined)
    .sort((a, b) => a.position - b.position)

  return {
    title: data.title,
    description: data.description ?? '',
    elements,
  }
}
